import type {RouteLocationNormalizedLoaded} from 'vue-router'
import type {AuthUser} from '@/stores/auth'
import {clubAPI} from '@/api/club'
import {seasonAPI} from '@/api/season'
import {tryoutHomeAPI, type TryoutHomeResponse} from '@/api/tryoutHome'
import {eventViewAccess, useEventViewStore} from '@/stores/eventView'

/** Recheck scoped access with the target session; never reuse the administrator's cache. */
export async function canKeepMasqueradeLocation(route: RouteLocationNormalizedLoaded, user: AuthUser, onEventHome?: (home:TryoutHomeResponse) => void, signal?:AbortSignal): Promise<boolean> {
  const systemAdmin = user.details?.systemAdmin === true
  if (['offline-capture','offline-capture-resume'].includes(String(route.name))) return false
  if (route.meta.requiresSystemAdmin && !systemAdmin) return false
  if (user.details?.updateDetails === true && route.path !== '/completeaccount') return false
  const clubId = String(route.params.clubId || ''), seasonId = String(route.params.seasonId || ''), eventId = String(route.params.tryoutId || '')
  try {
    if (eventId && seasonId && clubId) {
      const home = await (signal ? tryoutHomeAPI.loadHome(seasonId,eventId,clubId,signal) : tryoutHomeAPI.loadHome(seasonId,eventId,clubId))
      onEventHome?.(home)
      const access = eventViewAccess(home.event.roles)
      const homePath = `/clubhouse/${clubId}/season/${seasonId}/tryout/${eventId}`
      const isHome = route.path.replace(/\/$/,'') === homePath
      const required = route.meta.eventView === 'athlete' ? 'athlete' : 'staff'
      if (!(isHome || route.meta.eventView === 'shared' ? access.staff || access.athlete : access[required])) return false
      if (/\/setup(?:\/|$)/.test(route.path)) await (await import('@/api/tryoutSetup')).tryoutSetupAPI.loadSetupBundle(seasonId,eventId,clubId)
      else if (route.path.endsWith('/decision-room')) await (await import('@/api/decisionRoom')).decisionRoomAPI(clubId,seasonId,eventId).load(String(route.query.sessionId || 'all'))
      else if (route.path.endsWith('/program-settings')) await (await import('@/api/programSettings')).programSettingsAPI.load(seasonId,eventId,clubId)
      else if (/\/capture(?:\/|$)/.test(route.path)) await (await import('@/api/sessionCapture')).sessionCaptureAPI.loadWorkspace(seasonId,eventId,clubId)
      if (!isHome && route.meta.eventView !== 'shared') useEventViewStore().select({viewerId:user.id,clubId,seasonId,eventId},required,home.event.roles || {})
      return true
    }
    if (seasonId && clubId) { await seasonAPI.loadSeasonHome(seasonId,clubId); return true }
    if (clubId) {
      const {club} = await clubAPI.loadClubHome(clubId)
      if (route.query.tab === 'club-details') return club.owner === true || club.ownerId === user.id || systemAdmin
      if (route.query.tab === 'role-manager') return club.admin === true || club.owner === true
      return true
    }
    if (route.name === 'review-offer') { await (await import('@/api/offerRecipient')).offerRecipientAPI(String(route.params.participantId)).load(typeof route.query.offerId === 'string' ? route.query.offerId : null); return true }
    if (route.params.linkId) { await (await import('@/api/parents')).parentsAPI.view(String(route.params.linkId)); return true }
    // Personal account pages and unscoped navigation retain their normal route/page checks.
    return true
  } catch { return false }
}
